import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { Truck, Phone, Mail, MapPin, Clock, CheckCircle2, Link2 } from 'lucide-react';
import { useApi } from '@/hooks/useApi';

interface CoverageCity {
  city_id: string;
  city_name: Record<string, string>;
  eta_days: number;
}

interface PricingRate {
  from_city: Record<string, string>;
  to_city: Record<string, string>;
  service_type: string;
  price: number;
}

interface PricingList {
  id: string;
  name: string;
  default_delivery_fee: number;
  rates: PricingRate[];
}

interface CarrierProfile {
  id: string;
  name: string;
  logo_url: string | null;
  contact_info: { email?: string; phone?: string; address?: string } | null;
  is_guaranteed_payout: boolean;
  payout_duration_in_hours: number;
  coverage_cities: CoverageCity[];
  pricing_lists: PricingList[];
}

interface CarrierInfoModalProps {
  open: boolean;
  carrierId: string | null;
  isConnected: boolean;
  onClose: () => void;
  onConnect: (carrierId: string) => void;
  connectLoading: boolean;
}

export function CarrierInfoModal({
  open,
  carrierId,
  isConnected,
  onClose,
  onConnect,
  connectLoading,
}: CarrierInfoModalProps) {
  const { get, loading } = useApi({ showErrorToast: true });
  const [profile, setProfile] = useState<CarrierProfile | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    if (open && carrierId) {
      setProfile(null);
      setTermsAccepted(false);
      get(`/api/v1/carriers/${carrierId}/profile`).then((res) => {
        if (res) {
          // The profile endpoint may omit these collections — normalize to
          // arrays so downstream `.length` / `.map` never hit undefined.
          setProfile({
            ...res,
            coverage_cities: res.coverage_cities ?? [],
            pricing_lists: (res.pricing_lists ?? []).map((pl: any) => ({
              ...pl,
              rates: pl.rates ?? [],
            })),
          });
        }
      });
    }
  }, [open, carrierId]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        dir="rtl"
        aria-describedby={undefined}
      >
        {loading || !profile ? (
          <div className="py-8">
            {/* Keep an accessible title even while the profile loads */}
            <DialogTitle className="sr-only">معلومات شركة الشحن</DialogTitle>
            <LoadingSkeleton type="card" rows={3} />
          </div>
        ) : (
          <>
            {/* Header */}
            <DialogHeader className="text-end">
              <div className="flex items-center gap-3">
                {profile.logo_url ? (
                  <img
                    src={profile.logo_url}
                    alt={profile.name}
                    className="h-14 w-14 rounded-xl object-cover"
                  />
                ) : (
                  <div className="p-3 bg-primary/10 rounded-xl">
                    <Truck className="h-7 w-7 text-primary" />
                  </div>
                )}
                <div className="text-end">
                  <DialogTitle className="text-xl text-end">{profile.name}</DialogTitle>
                  {profile.is_guaranteed_payout && (
                    <div className="flex items-center gap-1 mt-1 text-sm text-blue-500">
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                      <span>دفع مضمون خلال {profile.payout_duration_in_hours} ساعة</span>
                    </div>
                  )}
                </div>
              </div>
            </DialogHeader>

            {/* Tabs */}
            <Tabs defaultValue="info" className="mt-2" dir="rtl">
              <TabsList className="w-full justify-start">
                <TabsTrigger value="info">معلومات</TabsTrigger>
                <TabsTrigger value="coverage">مناطق التغطية</TabsTrigger>
                <TabsTrigger value="pricing">الأسعار</TabsTrigger>
                <TabsTrigger value="terms">الشروط</TabsTrigger>
              </TabsList>

              {/* Info Tab */}
              <TabsContent value="info" className="space-y-3 mt-3">
                {profile.contact_info?.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span dir="ltr" className="text-end">{profile.contact_info.phone}</span>
                  </div>
                )}
                {profile.contact_info?.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span>{profile.contact_info.email}</span>
                  </div>
                )}
                {profile.contact_info?.address && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span>{profile.contact_info.address}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span>
                    {profile.is_guaranteed_payout
                      ? `دفع مضمون خلال ${profile.payout_duration_in_hours} ساعة`
                      : 'الدفع غير مضمون'}
                  </span>
                </div>
                {!profile.contact_info?.phone &&
                  !profile.contact_info?.email &&
                  !profile.contact_info?.address && (
                    <p className="text-sm text-muted-foreground">لا توجد معلومات اتصال متاحة</p>
                  )}
              </TabsContent>

              {/* Coverage Tab */}
              <TabsContent value="coverage" className="mt-3">
                {profile.coverage_cities.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    لا توجد مناطق تغطية محددة
                  </p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm" dir="rtl">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-end px-4 py-2.5 font-medium">المدينة</th>
                          <th className="text-end px-4 py-2.5 font-medium">مدة التوصيل</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profile.coverage_cities.map((city, i) => (
                          <tr key={city.city_id} className={i % 2 ? 'bg-muted/20' : ''}>
                            <td className="px-4 py-2.5 text-end">
                              {city.city_name?.ar || city.city_name?.en || '-'}
                            </td>
                            <td className="px-4 py-2.5 text-end">
                              <Badge variant="outline" className="text-xs">
                                {city.eta_days} {city.eta_days === 1 ? 'يوم' : 'أيام'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>

              {/* Pricing Tab */}
              <TabsContent value="pricing" className="mt-3 space-y-4">
                {profile.pricing_lists.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    لا توجد أسعار محددة
                  </p>
                ) : (
                  profile.pricing_lists.map((pl) => (
                    <Card key={pl.id} className="p-3">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-sm">{pl.name}</h4>
                        <Badge variant="secondary" className="text-xs">
                          الافتراضي: {formatCurrency(pl.default_delivery_fee)}
                        </Badge>
                      </div>
                      {pl.rates.length > 0 ? (
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full text-sm" dir="rtl">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="text-end px-3 py-2 font-medium">من</th>
                                <th className="text-end px-3 py-2 font-medium">إلى</th>
                                <th className="text-end px-3 py-2 font-medium">الخدمة</th>
                                <th className="text-end px-3 py-2 font-medium">السعر</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pl.rates.map((r, i) => (
                                <tr key={i} className={i % 2 ? 'bg-muted/20' : ''}>
                                  <td className="px-3 py-2 text-end">
                                    {r.from_city?.ar || r.from_city?.en || '-'}
                                  </td>
                                  <td className="px-3 py-2 text-end">
                                    {r.to_city?.ar || r.to_city?.en || '-'}
                                  </td>
                                  <td className="px-3 py-2 text-end">
                                    <Badge variant="outline" className="text-xs">
                                      {r.service_type || '-'}
                                    </Badge>
                                  </td>
                                  <td className="px-3 py-2 text-end font-medium">
                                    {formatCurrency(r.price)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">لا توجد أسعار مخصصة</p>
                      )}
                    </Card>
                  ))
                )}
              </TabsContent>

              {/* Terms Tab */}
              <TabsContent value="terms" className="mt-3">
                <Card className="p-4 space-y-3 text-sm leading-relaxed text-muted-foreground text-end">
                  <h4 className="font-medium text-foreground">الشروط والأحكام</h4>
                  <p>
                    بالتعاقد مع شركة الشحن، أنت توافق على الشروط التالية:
                  </p>
                  <ul className="list-disc space-y-1.5 me-5">
                    <li>يتم احتساب أسعار التوصيل وفقاً لجدول الأسعار المعتمد من شركة الشحن.</li>
                    <li>تلتزم شركة الشحن بالمواعيد المحددة للتوصيل حسب مناطق التغطية.</li>
                    <li>في حالة الدفع عند الاستلام، يتم تحويل المبالغ خلال المدة المحددة.</li>
                    <li>يحق لأي من الطرفين إلغاء التعاقد في أي وقت.</li>
                    <li>تخضع جميع الشحنات لسياسة التأمين والتعويض الخاصة بشركة الشحن.</li>
                  </ul>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Footer */}
            <DialogFooter className="mt-4 flex-col gap-3 sm:flex-col">
              {isConnected ? (
                <Badge className="bg-green-500/10 text-green-600 border-green-500/20 py-2 px-4 self-center">
                  <CheckCircle2 className="h-4 w-4 ms-2" />
                  متعاقد بالفعل
                </Badge>
              ) : (
                <>
                  <label className="flex items-center gap-2 cursor-pointer self-start">
                    <Checkbox
                      checked={termsAccepted}
                      onCheckedChange={(v) => setTermsAccepted(v === true)}
                    />
                    <span className="text-sm">أوافق على الشروط والأحكام</span>
                  </label>
                  <Button
                    className="w-full"
                    disabled={connectLoading || !termsAccepted}
                    onClick={() => carrierId && onConnect(carrierId)}
                  >
                    <Link2 className="h-4 w-4 ms-2" />
                    تعاقد
                  </Button>
                </>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
